/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Run Viewer — the marquee page (ADR-018 §19, §16).
 *
 * Fetches the run snapshot, opens the SSE stream, composes the Stage
 * Canvas + Event Log Panel, and surfaces collapsible Inputs / Outputs /
 * Artifacts at the bottom. ApprovalModal is wired in Slice 4 — Slice 3
 * just exposes the click handler.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import StageCanvas from './components/StageCanvas';
import EventLogPanel from './components/EventLogPanel';
import ApprovalModal from './components/ApprovalModal';
import DemoModeToggle from '../../components/workflows/DemoModeToggle';
import WorkflowDemoChrome from '../../components/workflows/WorkflowDemoChrome';
import useWorkflowEvents from '../../hooks/useWorkflowEvents';
import {
  displayCost,
  displayLatency,
  displayRunId,
  isDemoMode,
} from '../../utils/demoMode';
import './WorkflowsCatalog.css';
import './WorkflowDetail.css';
import './RunViewer.css';

function CollapsibleSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`wf-run-collapsible ${open ? 'is-open' : ''}`}>
      <button
        type="button"
        className="wf-run-collapsible-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="wf-run-collapsible-marker">{open ? '▾' : '▸'}</span>
        <span className="wf-run-collapsible-title">{title}</span>
      </button>
      {open && <div className="wf-run-collapsible-body">{children}</div>}
    </section>
  );
}

function ArtifactList({ artifacts }) {
  if (!Array.isArray(artifacts) || artifacts.length === 0) {
    return <p className="wf-run-empty">No artifacts produced.</p>;
  }
  return (
    <ul className="wf-run-artifacts">
      {artifacts.map((a, idx) => {
        const url = a.url || a.signed_url || a.location;
        const label = a.name || a.title || a.artifact_type || `artifact-${idx + 1}`;
        return (
          <li key={a.id || idx} className="wf-run-artifact-row">
            {url ? (
              <a href={url} target="_blank" rel="noopener noreferrer" className="wf-run-artifact-link">
                {label}
              </a>
            ) : (
              <span>{label}</span>
            )}
            {a.size_bytes && (
              <span className="wf-run-artifact-size">{(a.size_bytes / 1024).toFixed(1)} KB</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default function RunViewer() {
  const { id: runId } = useParams();
  const demo = isDemoMode();

  const {
    run,
    stages,
    events,
    pendingConfirmation,
    connection,
    error,
    clearPendingConfirmation,
    refreshSnapshot,
  } = useWorkflowEvents(runId);

  // Modal state. Two trigger paths:
  //   1. SSE → pendingConfirmation populates → auto-open
  //   2. Stage card Approve/Reject click → manualRequest sets the
  //      modal payload directly
  const [manualRequest, setManualRequest] = useState(null);
  const modalRequest = manualRequest || pendingConfirmation;
  const modalOpen = Boolean(modalRequest);

  // Auto-open the modal when SSE surfaces a confirmation request, but
  // don't override a manual selection that's mid-flow.
  useEffect(() => {
    if (pendingConfirmation && !manualRequest) {
      // No-op — modal opens via modalRequest selector.
    }
  }, [pendingConfirmation, manualRequest]);

  const handleApprovalClick = useCallback((payload) => {
    // Build a minimal request from the stage card click. If SSE already
    // surfaced richer context (tool description, inputs), merge it.
    const base = pendingConfirmation
      && pendingConfirmation.confirmation_id === payload.confirmation_id
      ? pendingConfirmation
      : {};
    setManualRequest({
      ...base,
      stage_id: payload.stage_id,
      confirmation_id: payload.confirmation_id,
      intent: payload.intent,
    });
  }, [pendingConfirmation]);

  const handleModalClose = useCallback(() => {
    setManualRequest(null);
    clearPendingConfirmation();
  }, [clearPendingConfirmation]);

  const handleModalResolved = useCallback(() => {
    // Refresh the run snapshot so the stage card stops showing
    // awaiting_confirmation even if the SSE event lags.
    refreshSnapshot();
  }, [refreshSnapshot]);

  if (error && !run) {
    return (
      <AdminLayout>
        <div className="wf-run-page">
          <Link to="/admin/workflows" className="wf-detail-back">← Catalog</Link>
          <div className={`wf-catalog-state wf-catalog-state-${error.kind}`}>
            <h2>Can't open this run</h2>
            <p>{error.message}</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const runState = String(run?.state || 'pending').toLowerCase();
  const workflowName = run?.workflow_name || run?.workflow_id || run?.workflow?.name;

  return (
    <AdminLayout>
      <div className="wf-run-page">
        <div className="wf-detail-topbar">
          <Link to="/admin/workflows" className="wf-detail-back">← Catalog</Link>
          <DemoModeToggle />
        </div>

        <header className="wf-run-header">
          <div className="wf-run-header-main">
            <h1 className="wf-run-title">
              {workflowName || 'Workflow run'}
            </h1>
            <div className="wf-run-header-meta">
              <span className={`wf-run-state-pill wf-run-state-${runState}`}>
                {runState}
              </span>
              {!demo && (
                <span className="wf-run-id">Run {displayRunId(runId, false)}</span>
              )}
              {run?.total_latency_ms != null && (
                <span className="wf-run-stat">
                  Total: {displayLatency(run.total_latency_ms, demo)}
                </span>
              )}
              {!demo && run?.cost_usd != null && (
                <span className="wf-run-stat">
                  Cost: {displayCost(run.cost_usd, false)}
                </span>
              )}
              {run?.initiated_by && !demo && (
                <span className="wf-run-stat">
                  By {run.initiated_by_name || displayRunId(run.initiated_by, true)}
                </span>
              )}
            </div>
          </div>
        </header>

        <main className="wf-run-body">
          <section className="wf-run-canvas-pane">
            <h2 className="wf-detail-pane-title">Stages</h2>
            <StageCanvas stages={stages} onApprovalClick={handleApprovalClick} />
            {pendingConfirmation && (
              <div className="wf-run-pending-banner">
                <strong>Action required:</strong>{' '}
                {pendingConfirmation.tool_description
                  || `Tool "${pendingConfirmation.tool_id}" requires confirmation.`}
                <button
                  type="button"
                  className="wf-btn wf-btn-secondary"
                  onClick={clearPendingConfirmation}
                >
                  Dismiss banner
                </button>
              </div>
            )}
          </section>

          <EventLogPanel events={events} connection={connection} />
        </main>

        <footer className="wf-run-footer">
          <CollapsibleSection title="Inputs">
            {run?.inputs ? (
              <pre className="wf-run-json">{JSON.stringify(run.inputs, null, 2)}</pre>
            ) : (
              <p className="wf-run-empty">No inputs recorded.</p>
            )}
          </CollapsibleSection>

          <CollapsibleSection title="Outputs" defaultOpen={runState === 'completed'}>
            {run?.outcome || run?.outputs ? (
              <pre className="wf-run-json">
                {JSON.stringify(run.outcome || run.outputs, null, 2)}
              </pre>
            ) : (
              <p className="wf-run-empty">No outputs yet.</p>
            )}
          </CollapsibleSection>

          <CollapsibleSection title="Artifacts">
            <ArtifactList artifacts={run?.artifacts} />
          </CollapsibleSection>
        </footer>

        <ApprovalModal
          open={modalOpen}
          runId={runId}
          request={modalRequest}
          onClose={handleModalClose}
          onResolved={handleModalResolved}
        />

        <WorkflowDemoChrome />
      </div>
    </AdminLayout>
  );
}
